// src/layout/Navbar.jsx  (или ваш путь)
import React, {
  Suspense,
  lazy,
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { decodeUserId } from "../../utils/getUserNameFromToken";
import { getStoredLanguage, normalizeLanguageCode } from "../../utils/language";

import { NotificationsContext } from "../../context/NotificationsContext";

import { openLoginModal, closeLoginModal } from "../../store/slices/uiSlice";
import { tokenStore } from "../../services/tokenStore";
import { fetchMyProfile } from "../../services/personalPageService";

import DesktopNavbar from "./DesktopNavbar";
import MobileNavbar from "./MobileNavbar";

const LoginModal = lazy(() => import("../LoginModal"));
const RegisterModal = lazy(() => import("../RegisterModal"));
const ForgotPasswordModal = lazy(
  () => import("../ForgotPasswordModal"),
);
const MenuModal = lazy(() => import("../MenuModal"));

const debounce = (fn, delay) => {
  let timerId;

  return (...args) => {
    window.clearTimeout(timerId);
    timerId = window.setTimeout(() => fn(...args), delay);
  };
};

function Navbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // ===== auth из Redux =====
  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);
  const isAuthLoading = useSelector(
    (s) => s.auth.isLoading || !s.auth.isResolved,
  );
  const user = useSelector((s) => s.auth.user); // ожидаем { id, username, role|roles? }

  // аватар из userSlice (если ведёте отдельно)
  const profilePictureUrl = useSelector((s) => s.user.profilePictureUrl);

  // ===== локальные стейты UI =====
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const authPromptRef = useRef(null);

  // поиск
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef(null);

  // нотификации
  const { unreadCount, markAllAsRead } = useContext(NotificationsContext);

  const loginModalOpen = useSelector((s) => s.ui?.loginModalOpen ?? false);
  const loginModalReason = useSelector((s) => s.ui?.loginModalReason ?? null);

  // страницы, где скрываем/меняем части UI
  const isBookDetailPage = /^\/book\/[^/]+$/.test(location.pathname);
  const isMessagesPage = location.pathname === "/messages";
  const token = tokenStore.get();
  const storeUser = useSelector((s) => s.auth.user); // { id, username, ... } если есть
  const currentUserId = storeUser?.id ?? decodeUserId(token); // <-- единый источник
  const userName =
    storeUser?.userName ?? storeUser?.username ?? storeUser?.name ?? "";

  // текущий язык
  const currentLang =
    useSelector((state) => state.language?.value) ||
    getStoredLanguage() ||
    normalizeLanguageCode(i18n.language);

  // ===== роль админа =====
  const isAdmin = useMemo(() => {
    try {
      if (!isLoggedIn || !token) return false;
      const payload = JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
      );
      const raw =
        payload[
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
        ] ??
        payload.role ??
        payload.roles;
      const roles = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return roles.includes("Admin");
    } catch {
      return false;
    }
  }, [isLoggedIn, token]);

  const handleNotificationsClick = async () => {
    try {
      await markAllAsRead([]);
      navigate("/notifications");
    } catch {}
  };

  // ===== поиск =====
  const debouncedSearch = useCallback(
    debounce(async (searchText) => {
      const trimmedText = searchText.trim();

      if (trimmedText.length < 3) {
        setSearchResults([]);
        setShowResults(false);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      try {
        const { searchBooksByQuery } = await import("../../services/bookService");
        const results = await searchBooksByQuery(trimmedText, t);

        setSearchResults(results);
        setShowResults(true);
      } catch (error) {
        setSearchResults([]);
        setShowResults(true);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [t],
  );

  const handleSearchChange = (e) => {
    const searchText = e.target.value;
    setQuery(searchText);
    debouncedSearch(searchText);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // prompt «нужно войти»
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        authPromptRef.current &&
        !authPromptRef.current.contains(event.target)
      ) {
        setShowAuthPrompt(false);
      }
    };
    if (showAuthPrompt) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAuthPrompt]);

  // ===== действия =====
  const openRegister = () => {
    dispatch(closeLoginModal());
    setIsRegisterModalOpen(true);
  };

  const openLogin = () => {
    setIsRegisterModalOpen(false);
    dispatch(openLoginModal());
  };

  const handleOpenLoginModal = () => {
    dispatch(openLoginModal());
  };

  const handleOpenRegisterModal = () => {
    setIsRegisterModalOpen(true);
  };

  // обновлённый хендлер
  const handleGoToCreateBook = async () => {
    try {
      const token = tokenStore.get();
      const resolvedUserId = (user && user.id) || decodeUserId(token);

      if (!isLoggedIn || !resolvedUserId) {
        dispatch(openLoginModal());
        return;
      }

      const profile = await fetchMyProfile();

      if (profile?.emailConfirmed) {
        navigate("/create-new-book");
      } else {
        alert(
          t("pleaseConfirmEmail") || "Подтвердите e-mail в настройках профиля.",
        );
      }
    } catch (err) {
      alert(
        t("errors.tryLater") ||
          "Не удалось проверить статус почты. Попробуйте позже.",
      );
    }
  };

  // ===== JSX =====
  return (
    <>
      <DesktopNavbar
        navigate={navigate}
        t={t}
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        searchContainerRef={searchContainerRef}
        query={query}
        handleSearchChange={handleSearchChange}
        isSearching={isSearching}
        showResults={showResults}
        searchResults={searchResults}
        currentLang={currentLang}
        isAuthLoading={isAuthLoading}
        profilePictureUrl={profilePictureUrl}
        userName={userName}
        handleGoToCreateBook={handleGoToCreateBook}
        unreadCount={unreadCount}
        handleNotificationsClick={handleNotificationsClick}
        openLoginModal={handleOpenLoginModal}
        openRegisterModal={handleOpenRegisterModal}
      />

      <MobileNavbar
        navigate={navigate}
        t={t}
        isBookDetailPage={isBookDetailPage}
        isMessagesPage={isMessagesPage}
        isLoggedIn={isLoggedIn}
        query={query}
        handleSearchChange={handleSearchChange}
        searchContainerRef={searchContainerRef}
        isSearching={isSearching}
        showResults={showResults}
        searchResults={searchResults}
        currentLang={currentLang}
        unreadCount={unreadCount}
        handleNotificationsClick={handleNotificationsClick}
        openAuthPrompt={() => setShowAuthPrompt(true)}
        openMenuModal={() => setMenuModalOpen(true)}
      />

      <Suspense fallback={null}>
        {loginModalOpen && (
          <LoginModal
            onClose={() => dispatch(closeLoginModal())}
            openRegister={openRegister}
            openForgotPassword={() => setShowForgotPasswordModal(true)}
            reason={loginModalReason}
          />
        )}

        {showForgotPasswordModal && (
          <ForgotPasswordModal
            onClose={() => setShowForgotPasswordModal(false)}
          />
        )}

        {isRegisterModalOpen && (
          <RegisterModal
            onClose={() => setIsRegisterModalOpen(false)}
            openLogin={openLogin}
          />
        )}
      </Suspense>

      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div
            ref={authPromptRef}
            className="bg-[#1C1C1C] p-4 rounded-md shadow-md max-w-xs text-center border-[0.5px] rounded-md border-[#4A4A4D] !border-solid"
          >
            <p className="text-[#AAA] mb-4">{t("authRequired")}</p>
            <div className="flex justify-around">
              <button
                className="bg-[#F86240] text-white px-4 py-2 rounded font-bold"
                onClick={() => {
                  setShowAuthPrompt(false);
                  handleOpenLoginModal();
                }}
              >
                {t("navbar.login")}
              </button>
              <button
                className="bg-[#242425] border-[0.5px] rounded-md border-[#4A4A4D] !border-solid text-white px-4 py-2 rounded"
                onClick={() => setShowAuthPrompt(false)}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        {menuModalOpen && (
          <MenuModal
            isOpen={menuModalOpen}
            onClose={() => setMenuModalOpen(false)}
            openLoginModal={handleOpenLoginModal}
            openRegisterModal={handleOpenRegisterModal}
            isAdmin={isAdmin}
            handleNotificationsClick={handleNotificationsClick}
            unreadCount={unreadCount}
          />
        )}
      </Suspense>
    </>
  );
}

export default Navbar;
